import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { USER_LOGIN_SUCCESS } from '../../constants/userConstants';
import { Button, Alert } from 'antd';

const MicrosoftOAuth = ({ onLoginSuccess, onLoginFail }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listening, setListening] = useState(false);
  const [loginWindow, setLoginWindow] = useState(null);
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  function camelize(str) {
    return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    }).replace(/\s+/g, '');
  }

  const convertKeysToCamelCase = (item) => {
    if (Array.isArray(item)) {
      return item.map(convertKeysToCamelCase);
    } else if (item !== null && typeof item === 'object') {
      return Object.keys(item).reduce((acc, key) => {
        acc[camelize(key)] = convertKeysToCamelCase(item[key]);
        return acc;
      }, {});
    }
    return item;
  };

  const handleMicrosoftLogin = () => {
    setIsLoading(true);
    setError(null);
    setIsPopupBlocked(false);
    setListening(false);
    try {
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2.5;
      const authUrl = '/dotnet_api/v1/socialauth/microsoft';
      const popup = window.open(
        authUrl,
        'microsoftAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      if (!popup || popup.closed) {
        setIsPopupBlocked(true);
        setIsLoading(false);
        return;
      }
      setLoginWindow(popup);
      setListening(true);
    } catch (err) {
      setError(`Failed to open authentication window: ${err.message}`);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleMessage = async (event) => {
      try {
        const data = event.data;
        if (data && data.Token) {
          const normalizedData = convertKeysToCamelCase(data);
          localStorage.setItem('userInfo', JSON.stringify(normalizedData));
          localStorage.setItem('2fauthenticated', 'true');
          dispatch({
            type: USER_LOGIN_SUCCESS,
            payload: normalizedData
          });
          if (loginWindow) loginWindow.close();
          setListening(false);
          setIsLoading(false);
          if (onLoginSuccess) onLoginSuccess(normalizedData);
          setTimeout(() => {
            navigate('/user_dashboard');
          }, 100);
        } else if (data && data.error) {
          setError(data.error);
          setIsLoading(false);
          setListening(false);
          if (onLoginFail) onLoginFail(data.error);
        }
      } catch (err) {
        setError(`Error processing authentication response: ${err.message}`);
        setIsLoading(false);
        setListening(false);
        if (onLoginFail) onLoginFail(err.message);
      }
    };

    const checkLoginStatus = () => {
      if (!listening) return;
      if (loginWindow && loginWindow.closed) {
        setListening(false);
        setIsLoading(false);
        setError('Login window was closed or no account was selected. Please try again.');
        if (onLoginFail) onLoginFail('Login window was closed or no account was selected.');
      }
    };

    let interval;
    if (listening) {
      window.addEventListener('message', handleMessage);
      interval = setInterval(checkLoginStatus, 1000);
    }
    return () => {
      window.removeEventListener('message', handleMessage);
      if (interval) clearInterval(interval);
    };
  }, [listening, loginWindow, dispatch, navigate, onLoginSuccess, onLoginFail]);

  const MicrosoftIcon = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 23 23" 
      style={{ marginRight: '8px' }}
    >
      <path fill="#f25022" d="M1 1h10v10H1z"/>
      <path fill="#00a4ef" d="M1 12h10v10H1z"/>
      <path fill="#7fba00" d="M12 1h10v10H12z"/>
      <path fill="#ffb900" d="M12 12h10v10H12z"/>
    </svg>
  );

  return (
    <div style={{ width: '100%' }}>
      <Button
        onClick={handleMicrosoftLogin}
        loading={isLoading}
        icon={<MicrosoftIcon />}
        size="large"
        style={{ 
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '40px'
        }}
        disabled={isLoading || listening}
      >
        Continue with Microsoft
      </Button>
      {isPopupBlocked && (
        <Alert
          message="Pop-up Blocked"
          description="Please allow pop-ups for this site and try again."
          type="warning"
          showIcon
          style={{ marginTop: '10px' }}
        />
      )}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: '10px' }}
        />
      )}
    </div>
  );
};

export default MicrosoftOAuth; 